/**
 * Форматтеры и подсказки для раздела «Исполнительные листы» (ИЛ-1b).
 *
 * Чистые функции без DOM — чтобы их покрывал unit-тест (tests/unit/sheets-format.test.mjs)
 * и чтобы вид (views/sheets.js) и тесты считали одно и то же. Подсказки статуса,
 * стадии ПД и вида работ — константы здесь (статус/стадия/вид — TEXT без CHECK на
 * бэкенде, см. ИЛ-1a): это варианты для datalist, пользователь может ввести свой.
 */

// Подсказки статуса ИЛ (как в 1С; плюс варианты из 1С при переносе — вводятся вручную).
export const SHEET_STATUSES = [
  'Создание',
  'Расчет',
  'В работе',
  'Приостановка',
  'Сдано по накладной',
  'Закрыт',
];

// Статус по умолчанию у нового листа (как в 1С).
export const DEFAULT_STATUS = 'Создание';

// Статусы, при которых лист неактуален (совпадает с backend sheet_keys.CLOSED_STATUSES).
export const CLOSED_STATUSES = ['Закрыт', 'Сдано по накладной'];

export const PD_STAGES = [
  'РД и ПД',
  'Проектная документация',
  'Рабочая документация',
  'Инженерные изыскания',
];

export const WORK_KINDS = [
  'Новое строительство',
  'Реконструкция',
  'Обследование',
  'Капитальный ремонт',
];

/** DD.MM.YYYY из ISO-даты (или «—»). */
export const fmtDate = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('.') : '—');

/** YYYY-MM-DD для <input type="date"> (или пусто). */
export const fmtDateInput = (v) => (v ? String(v).slice(0, 10) : '');

/** Да / Нет / — для tri-state bool. */
export const actualLabel = (v) => (v == null ? '—' : v ? 'Да' : 'Нет');

/** Да / — для bool (пусто → прочерк). */
export const yesNo = (v) => (v ? 'Да' : '—');

/**
 * Стоимость: группировка разрядов неразрывным пробелом, запятая-десятичная, « ₽».
 * Детерминированно (без Intl), чтобы unit-тест не зависел от ICU-локали рантайма.
 */
export const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const sign = n < 0 ? '−' : '';
  const [int, frac] = Math.abs(n).toFixed(2).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${sign}${grouped},${frac} ₽`;
};

/** Заголовок карточки листа = № ИП (иначе наименование сокр., иначе «Лист #id»). */
export const sheetTitle = (s) => s.ip_no || s.short_name || `Лист #${s.id}`;

/** Пустое значение (null / '' ) — для «нулы в конец» при сортировке. */
const isEmpty = (v) => v == null || v === '';

/**
 * Клиентская сортировка листов по ключу колонки (как в data-table: «status» / «-status»).
 * Строки — localeCompare('ru'); числа — численно; пустые всегда в конце; тай-брейк по id.
 * Серверного sort у /sheets нет (ИЛ-1b бэкенд не трогает), а листов немного — весь
 * отфильтрованный набор грузится разом и сортируется здесь.
 */
export function sortSheets(items, sort) {
  if (!sort) return items;
  const desc = sort.startsWith('-');
  const key = sort.replace(/^-/, '');
  const dir = desc ? -1 : 1;
  return [...items].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (isEmpty(av) && isEmpty(bv)) return (a.id ?? 0) - (b.id ?? 0);
    if (isEmpty(av)) return 1; // нулы в конец независимо от направления
    if (isEmpty(bv)) return -1;
    let cmp;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv), 'ru');
    return cmp * dir || (a.id ?? 0) - (b.id ?? 0);
  });
}

/** Отфильтровать листы по выбранным статусам (клиентский мульти-фильтр). */
export function filterByStatus(items, statuses) {
  if (!statuses || !statuses.length) return items;
  const set = new Set(statuses);
  return items.filter((s) => set.has(s.status ?? ''));
}
